import { Data } from "effect";
import type { BaseDomainErrorData } from "./DomainError";

/**
 * Invoice-specific domain errors
 */

/**
 * Error thrown when invoice number format is invalid
 */
export class InvoiceNumberFormatError extends Data.TaggedError("@/InvoiceNumberFormatError")<
  BaseDomainErrorData & {
    readonly invoiceNumber: string;
    readonly expectedFormat: string;
    readonly pattern: string;
  }
> {}

/**
 * Error thrown when invoice number already exists
 */
export class InvoiceNumberConflictError extends Data.TaggedError("@/InvoiceNumberConflictError")<
  BaseDomainErrorData & {
    readonly affectedInvoiceNumber: string;
    readonly existingInvoiceId?: string;
  }
> {}

/**
 * Error thrown when invoice validation fails
 */
export class InvoiceValidationError extends Data.TaggedError("@/InvoiceValidationError")<
  BaseDomainErrorData & {
    readonly invoiceId?: string;
    readonly validationErrors: string[];
  }
> {}

/**
 * Error thrown when attempting to modify immutable invoice
 */
export class InvoiceImmutabilityError extends Data.TaggedError("@/InvoiceImmutabilityError")<
  BaseDomainErrorData & {
    readonly invoiceId: string;
    readonly attemptedOperation: string;
  }
> {}

/**
 * Order-specific domain errors
 */

/**
 * Error thrown when order is not found
 */
export class OrderNotFoundError extends Data.TaggedError("@/OrderNotFoundError")<
  BaseDomainErrorData & {
    readonly orderId: string;
    readonly searchCriteria?: Record<string, unknown>;
  }
> {}

/**
 * Error thrown when order validation fails
 */
export class OrderValidationError extends Data.TaggedError("@/OrderValidationError")<
  BaseDomainErrorData & {
    readonly orderId: string;
    readonly validationErrors: string[];
  }
> {}

/**
 * Error thrown when order is in invalid state for operation
 */
export class OrderStateError extends Data.TaggedError("@/OrderStateError")<
  BaseDomainErrorData & {
    readonly orderId: string;
    readonly currentState: string;
    readonly requiredState: string;
    readonly operation: string;
  }
> {}

/**
 * PDF and document-specific errors
 */

/**
 * Error thrown when PDF generation fails
 */
export class PDFGenerationError extends Data.TaggedError("@/PDFGenerationError")<
  BaseDomainErrorData & {
    readonly invoiceId?: string;
    readonly invoiceNumber?: string;
    readonly generationStep?: string;
  }
> {}

/**
 * Error thrown when PDF compliance check fails
 */
export class PDFComplianceError extends Data.TaggedError("@/PDFComplianceError")<
  BaseDomainErrorData & {
    readonly complianceStandard: string;
    readonly complianceIssue: string;
    readonly invoiceId?: string;
  }
> {}

/**
 * Storage-specific errors
 */

/**
 * Error thrown when storage operation fails
 */
export class StorageError extends Data.TaggedError("@/StorageError")<
  BaseDomainErrorData & {
    readonly operation: string;
    readonly storageLocation?: string;
    readonly statusCode?: number;
    readonly retryable: boolean;
  }
> {}

/**
 * Error thrown when storage upload fails
 */
export class StorageUploadError extends Data.TaggedError("@/StorageUploadError")<
  BaseDomainErrorData & {
    readonly fileName: string;
    readonly fileSize: number;
    readonly storageProvider: string;
    readonly retryAttempt: number;
    readonly maxRetries: number;
  }
> {}

/**
 * Repository and persistence errors
 */

/**
 * Error thrown when repository operation fails
 */
export class RepositoryError extends Data.TaggedError("@/RepositoryError")<
  BaseDomainErrorData & {
    readonly repository: string;
    readonly operation: string;
    readonly entityType?: string;
    readonly entityId?: string;
  }
> {}

/**
 * Error thrown when database constraint is violated
 */
export class DatabaseConstraintError extends Data.TaggedError("@/DatabaseConstraintError")<
  BaseDomainErrorData & {
    readonly constraintName: string;
    readonly constraintType: "unique" | "foreign_key" | "check" | "not_null";
    readonly tableName: string;
    readonly columnName?: string;
  }
> {}
